define('businessServices/signup/signupEntryGate',[
    'businessServices/ipQualityScore/ipQualityScore',
    'businessServices/router/router',
    'businessServices/signup/affiliateSignup',
    'businessServices/signup/signupCookieStore',
    'businessServices/signup/signupSessionStore',
    'common/authentication/authenticatedSignup',
    'common/promises/promiseFactory'
], function() {

    const PromiseFactoryConstructor = require('common/promises/promiseFactory');
    const _promiseFactory = new PromiseFactoryConstructor();

    // The device screening runs on its own factory and never has a timeout applied. Two reasons it
    // cannot share the caller's factory: a caller that waits on its own promises would block first
    // paint on IPQualityScore, and deferIndefinitely still enrolls the promise in the factory's
    // wait list, so sharing would turn "no timeout" into "the route never finishes activating".
    const _backgroundPromiseFactory = new PromiseFactoryConstructor();

    const VERDICT_REASON__FRAUDULENT_EMAIL = "fraudulentEmail";
    const VERDICT_REASON__FRAUDULENT_DEVICE = "fraudulentDevice";

    // Query parameters read as a fallback for the attribution cookies.
    const ATTRIBUTION_QUERY_PARAMETERS = {
        googleClickId: 'gclid',
        utmSource: 'utm_source',
        utmMedium: 'utm_medium',
        utmCampaign: 'utm_campaign',
        utmTerm: 'utm_term',
        utmContent: 'utm_content'
    };

    let _authenticatedSignup = null;
    let _affiliateSignup = null;
    let _ipQualityScore = null;
    let _router = null;
    let _signupCookieStore = null;
    let _signupSessionStore = null;

    // State lives here rather than on the instance because begin() and evaluate() are called from
    // two different routes, and therefore two different view models. The in-flight IPQualityScore
    // promises have to outlive the first of them for the head start to be worth anything.
    let _begunForSignupSessionId = null;
    let _deviceFingerPrintSave = null;
    let _deviceFraudCheck = null;
    let _isFraudulentDevice = false;

    // Attribution query parameters seen on any signup route during this page load.
    let _queryStringAttribution = {};

    // The normalized email the HubSpot attribution form was last pushed for.
    let _attributionEmailAddress = null;

    // The push currently on the wire.
    let _attributionPushInFlight = null;

    const _saveDeviceFingerPrint = () => {
        return _promiseFactory.deferIndefinitely((promise) => {
            _ipQualityScore.waitForInitComplete()
                .done(() => {
                    const deviceFingerPrint = _ipQualityScore.getDeviceId();
                    if (!deviceFingerPrint) {
                        promise.resolve();
                        return;
                    }

                    _signupSessionStore.saveDeviceFingerPrint(deviceFingerPrint)
                        .fail(promise.resolve)
                        .done(promise.resolve);
                });
        });
    };

    const _checkIsFraudulentDevice = () => {
        return _promiseFactory.deferIndefinitely((promise) => {
            _ipQualityScore.waitForInitComplete()
                .done(() => {
                    const requestId = _ipQualityScore.getRequestId();
                    _signupSessionStore.checkIsFraudulentDeviceOrIp(requestId)
                        .fail(promise.resolve)
                        .done((isFraudulentDevice) => {
                            _isFraudulentDevice = isFraudulentDevice;
                            promise.resolve();
                        });
                });
        });
    };

    const _saveResellerInfo = () => {
        return _promiseFactory.deferIndefinitely((promise) => {
            _affiliateSignup.get()
                .fail(promise.resolve)
                .done(({resellerId, clickId}) => {
                    if (!resellerId && !clickId) {
                        promise.resolve();
                        return;
                    }

                    _signupSessionStore.saveResellerAndClickId(resellerId, clickId)
                        .fail(promise.resolve)
                        .done(promise.resolve);
                });
        });
    };

    const _normalizeEmailAddress = (emailAddress) => (emailAddress || '').trim().toLowerCase();

    const _captureQueryStringAttribution = () => {
        const instruction = _router.activeInstruction();
        const queryParams = instruction && instruction.queryParams ? instruction.queryParams : {};

        Object.keys(ATTRIBUTION_QUERY_PARAMETERS).forEach((key) => {
            const value = queryParams[ATTRIBUTION_QUERY_PARAMETERS[key]];
            if (value && !_queryStringAttribution[key]) {
                _queryStringAttribution[key] = String(value);
            }
        });
    };

    const _pushAttributionInfo = () => {
        return _promiseFactory.deferIndefinitely((promise) => {
            _signupCookieStore.get()
                .fail(promise.resolve)
                .done((cookieAttribution) => {
                    const attribution = Object.assign({}, cookieAttribution);
                    Object.keys(ATTRIBUTION_QUERY_PARAMETERS).forEach((key) => {
                        if (!attribution[key] && _queryStringAttribution[key]) {
                            attribution[key] = _queryStringAttribution[key];
                        }
                    });

                    const { affiliateUrl, hubSpotUtk, googleClickId, utmSource, utmMedium, utmCampaign, utmTerm, utmContent } = attribution;

                    _signupSessionStore.saveAttributionInfo(affiliateUrl, hubSpotUtk, googleClickId, utmSource, utmMedium, utmCampaign, utmTerm, utmContent)
                        .fail(() => promise.resolve(false))
                        .done(() => promise.resolve(true));
                });
        });
    };

    const _saveAttributionInfo = (emailAddress) => {
        const normalizedEmailAddress = _normalizeEmailAddress(emailAddress);

        if (normalizedEmailAddress && normalizedEmailAddress === _attributionEmailAddress) {
            return _promiseFactory.deferIndefinitely((promise) => promise.resolve());
        }

        if (_attributionPushInFlight && _attributionPushInFlight.emailAddress === normalizedEmailAddress) {
            return _attributionPushInFlight.promise;
        }

        const inFlight = {emailAddress: normalizedEmailAddress, promise: null};
        inFlight.promise = _promiseFactory.deferIndefinitely((promise) => {
            _pushAttributionInfo()
                .done((isPushed) => {
                    if (isPushed) {
                        _attributionEmailAddress = normalizedEmailAddress;
                    }
                    if (_attributionPushInFlight === inFlight) {
                        _attributionPushInFlight = null;
                    }
                    promise.resolve();
                });
        });
        _attributionPushInFlight = inFlight;

        return inFlight.promise;
    };

    // For callers that have a saved email before screening runs - pushes as soon as
    // the email field is valid.
    const _saveAttribution = (emailAddress) => {
        _begin();
        return _saveAttributionInfo(emailAddress);
    };

    // Returns the reseller save so a caller can wait on it. The IPQualityScore work deliberately
    // is not part of that promise - it runs in the background for the rest of the flow. Waiting on
    // the reseller save matters because it writes the same signup session document the step's own
    // save does, and letting the two race risks one clobbering the other.
    const _begin = () => {
        _captureQueryStringAttribution();

        const signupSessionId = _authenticatedSignup.signupSessionId;
        if (_begunForSignupSessionId === signupSessionId) {
            return _promiseFactory.deferIndefinitely((promise) => promise.resolve());
        }

        _begunForSignupSessionId = signupSessionId;
        _isFraudulentDevice = false;
        _attributionEmailAddress = null;
        _attributionPushInFlight = null;

        _deviceFingerPrintSave = _backgroundPromiseFactory.deferIndefinitely((promise) => {
            _saveDeviceFingerPrint()
                .fail(promise.resolve)
                .done(promise.resolve);
        });

        _deviceFraudCheck = _backgroundPromiseFactory.deferIndefinitely((promise) => {
            _checkIsFraudulentDevice()
                .fail(promise.resolve)
                .done(promise.resolve);
        });

        return _saveResellerInfo();
    };

    const _awaitDeviceScreening = () => {
        return _promiseFactory.deferIndefinitely((promise) => {
            // Both calls are already in flight, so waiting on them one after the other costs the
            // slower of the two rather than the sum.
            const awaitDeviceFingerPrint = () => {
                _deviceFingerPrintSave
                    .fail(promise.resolve)
                    .done(promise.resolve);
            };

            _deviceFraudCheck
                .fail(awaitDeviceFingerPrint)
                .done(awaitDeviceFingerPrint);
        });
    };

    const _evaluate = (emailAddress) => {
        return _promiseFactory.deferIndefinitely((promise) => {
            // A customer who refreshes on, or deep-links to, a later step never painted the route
            // that calls begin(). Starting here costs the full IPQualityScore wait, which is the
            // correct trade against screening nobody.
            _begin();

            const screenSignup = () => {
                _signupSessionStore.isFraudulentEmailAddress(emailAddress)
                    .fail(() => promise.resolve({isAllowed: true, reason: null}))
                    .done((isFraudulentEmailAddress) => {
                        if (isFraudulentEmailAddress) {
                            promise.resolve({isAllowed: false, reason: VERDICT_REASON__FRAUDULENT_EMAIL});
                            return;
                        }

                        _awaitDeviceScreening()
                            .done(() => {
                                if (_isFraudulentDevice === true) {
                                    promise.resolve({isAllowed: false, reason: VERDICT_REASON__FRAUDULENT_DEVICE});
                                    return;
                                }

                                promise.resolve({isAllowed: true, reason: null});
                            });
                    });
            };

            _saveAttributionInfo(emailAddress)
                .fail(screenSignup)
                .done(screenSignup);
        });
    };

    const _reset = () => {
        _begunForSignupSessionId = null;
        _deviceFingerPrintSave = null;
        _deviceFraudCheck = null;
        _isFraudulentDevice = false;
        _queryStringAttribution = {};
        _attributionEmailAddress = null;
        _attributionPushInFlight = null;
    };

    const _init = () => {
        _authenticatedSignup = require('common/authentication/authenticatedSignup');
        _ipQualityScore = require('businessServices/ipQualityScore/ipQualityScore');
        _router = require('businessServices/router/router');

        const SignupSessionStoreConstructor = require('businessServices/signup/signupSessionStore');
        _signupSessionStore = new SignupSessionStoreConstructor();
        _signupSessionStore.init();

        const AffiliateSignupConstructor = require('businessServices/signup/affiliateSignup');
        _affiliateSignup = new AffiliateSignupConstructor();
        _affiliateSignup.init();

        const SignupCookieStoreConstructor = require('businessServices/signup/signupCookieStore');
        _signupCookieStore = new SignupCookieStoreConstructor();
        _signupCookieStore.init();
    };

    return function() {
        let self = this;

        self.init = _init;
        self.begin = _begin;
        self.evaluate = _evaluate;
        self.saveAttribution = _saveAttribution;
        self.reset = _reset;

        self.verdictReasons = {
            fraudulentEmail: VERDICT_REASON__FRAUDULENT_EMAIL,
            fraudulentDevice: VERDICT_REASON__FRAUDULENT_DEVICE
        };
    };
});

